import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { ClearCacheButton } from '@/components/ClearCacheButton';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Filter,
  Eye,
  Edit,
  Calendar,
  BarChart3,
  RefreshCw,
  Database
} from 'lucide-react';
import type { Statistic } from '@shared/schema';

export default function AdminStatistics() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Redirect if not authenticated or not admin
  useEffect(() => {
    if (!isLoading && (!isAuthenticated || user?.role !== 'admin')) {
      toast({
        title: t('notifications.unauthorized'),
        description: "You don't have permission to access this page",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = isAuthenticated ? "/" : "/api/login";
      }, 500);
      return;
    }
  }, [isLoading, isAuthenticated, user, toast, t]);

  // Fetch all statistics for admin
  const { data: statistics = [], isLoading: isLoadingStats, error } = useQuery<Statistic[]>({
    queryKey: ['/api/statistics/all'],
    queryFn: async () => {
      const response = await fetch('/api/statistics/all', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      return response.json();
    },
    enabled: isAuthenticated && user?.role === 'admin'
  });

  // Filter statistics
  const filteredStatistics = statistics.filter(stat => {
    const matchesSearch = stat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         stat.lead?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || stat.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || stat.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/statistics/all'] });
    toast({
      title: "Datos Actualizados",
      description: "Lista de estadísticas refrescada",
    });
  };

  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      published: 'default',
      draft: 'secondary',
      archived: 'destructive'
    } as const;
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Database className="h-8 w-8" />
            Gestión de Estadísticas
          </h1>
          <p className="text-muted-foreground mt-2">
            Administra y gestiona todas las estadísticas del sistema
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refrescar
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por título o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Estado</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="all">Todos</option>
                <option value="published">Publicado</option>
                <option value="draft">Borrador</option>
                <option value="archived">Archivado</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Categoría</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="all">Todas</option>
                <option value="society">Sociedad</option>
                <option value="politics">Política</option>
                <option value="economy">Economía</option>
                <option value="culture">Cultura</option>
                <option value="technology">Tecnología</option>
                <option value="entertainment">Entretenimiento</option>
                <option value="sports">Deportes</option>
                <option value="science">Ciencia</option>
                <option value="lifestyle">Estilo de Vida</option>
                <option value="unclassified">Sin Clasificar</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics List */}
      <div className="space-y-4">
        {isLoadingStats ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-destructive">Error al cargar las estadísticas</p>
            </CardContent>
          </Card>
        ) : filteredStatistics.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No se encontraron estadísticas</p>
            </CardContent>
          </Card>
        ) : (
          filteredStatistics.map((statistic) => (
            <Card key={statistic.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{statistic.title}</h3>
                      {getStatusBadge(statistic.status)}
                      {statistic.featured && (
                        <Badge variant="outline" className="text-yellow-600">
                          Destacado
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-muted-foreground mb-3 line-clamp-2">
                      {statistic.lead || 'Sin descripción'}
                    </p>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(statistic.createdAt)}
                      </div>
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-4 w-4" />
                        {statistic.category || 'Sin categoría'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {statistic.viewCount || 0} vistas
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/statistics/${statistic.slug}`)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    
                    <ClearCacheButton
                      statisticId={statistic.id}
                      statisticTitle={statistic.title}
                      onCacheCleared={() => {
                        toast({
                          title: "Cache Limpiado",
                          description: `Cache limpiado para: ${statistic.title}`,
                        });
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Stats Summary */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Resumen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {statistics.filter(s => s.status === 'published').length}
              </div>
              <div className="text-sm text-muted-foreground">Publicados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {statistics.filter(s => s.status === 'draft').length}
              </div>
              <div className="text-sm text-muted-foreground">Borradores</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {statistics.filter(s => s.status === 'archived').length}
              </div>
              <div className="text-sm text-muted-foreground">Archivados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {statistics.filter(s => s.featured).length}
              </div>
              <div className="text-sm text-muted-foreground">Destacados</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
